import {
  getManagerProfilesRetrieveQueryKey,
  getStafferGigsV2CalendarListQueryKey,
  getStafferGigsV2RecurringPatternGigsListQueryKey,
  getStafferTosAcceptanceRetrieveQueryKey,
  getUsersApprovedLocationsListQueryKey,
  getUsersRetrieveQueryKey,
} from "@sidegigllc/api/client";
import { ApiError } from "@sidegigllc/api/resources";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activeLocationsQueryOptions,
  locationQueryOptions,
  organizationsQueryOptions,
  useContextLocations,
} from "api/queries/locations";
import { useAuth, useIsAdmin } from "domains/auth";
import { AUTH_QUERY_KEY } from "domains/auth/queries/session";
import { useAtom } from "jotai";
import { identify } from "providers/LaunchDarkly/client";
import React from "react";
import { isDeepEqual } from "remeda";
import { activeLocationIdAtom } from "./atoms";
import { useActiveLocationId } from "./locationOrgContext";

const isCalendarGigsQuery = (queryKey: ReadonlyArray<unknown>) =>
  queryKey[0] === getStafferGigsV2CalendarListQueryKey()[0] ||
  queryKey[0] === getStafferGigsV2RecurringPatternGigsListQueryKey()[0];

export const useAuthenticatedStateSetup = () => {
  const queryClient = useQueryClient();
  const [storedLocationId, setStoredLocationId] = useAtom(activeLocationIdAtom);
  const [viewLocationId] = useActiveLocationId();

  const { data: locations, isLoading: locationsLoading } = useContextLocations({ includeAll: true });
  const viewLocation = useQuery({
    ...locationQueryOptions(viewLocationId ?? -1),
    enabled: !!viewLocationId,
  }).data;
  // Gate the loader on the stored location, NOT the view: an inaccessible URL location
  // 403s for non-admins and the view query never resolves, hanging the shell forever.
  const { data: storedLocation, error: storedLocationError } = useQuery({
    ...locationQueryOptions(storedLocationId ?? -1),
    enabled: !!storedLocationId,
    retry: (count, error) => !ApiError.fromThrown(error).isNotFound() && count < 3,
  });
  const storedLocationMissing = !!storedLocationError && ApiError.fromThrown(storedLocationError).isNotFound();

  const { user, analyticsUser } = useAuth();
  const isAdmin = useIsAdmin();

  // Clear the previous location's cached data when SWITCHING locations. Skip the initial
  // establishment (undefined → id): there's nothing stale to clear (persisted queries are all
  // excluded below) and resetting then would cancel the in-flight loader prefetches we just kicked off.
  const previousViewLocationId = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (!viewLocationId) return;
    const previous = previousViewLocationId.current;
    previousViewLocationId.current = viewLocationId;
    if (previous === undefined || previous === viewLocationId) return;
    queryClient.resetQueries({
      predicate: ({ queryKey }) => {
        const queryKeyFirst = queryKey[0];
        const isAuthQuery = queryKey === AUTH_QUERY_KEY;
        const isManagerQuery = isDeepEqual(queryKey, getManagerProfilesRetrieveQueryKey("me"));
        const isAuthUserQuery = isDeepEqual(queryKey, getUsersRetrieveQueryKey("me"));
        const isLocationQuery =
          // list keys are ["/api/locations/", params], infinite keys are ["infinite", "/api/locations/", params]
          queryKey.slice(0, 2).some((key: unknown) => typeof key === "string" && key.startsWith("/api/locations/")) ||
          queryKeyFirst === activeLocationsQueryOptions().queryKey[0] ||
          queryKeyFirst === getUsersApprovedLocationsListQueryKey("me")[0];
        const isOrgsQuery = queryKeyFirst === organizationsQueryOptions().queryKey[0];
        const isTosQuery = isDeepEqual(queryKey, getStafferTosAcceptanceRetrieveQueryKey("me"));
        return (
          !isAuthQuery &&
          !isManagerQuery &&
          !isAuthUserQuery &&
          !isLocationQuery &&
          !isOrgsQuery &&
          !isTosQuery &&
          !isCalendarGigsQuery(queryKey)
        );
      },
    });
    queryClient.invalidateQueries({ predicate: ({ queryKey }) => isCalendarGigsQuery(queryKey) });
  }, [viewLocationId, queryClient]);

  React.useEffect(() => {
    if (!locations || !user) return;
    if (locations.length === 0) {
      if (storedLocationId !== undefined) setStoredLocationId(undefined);
      return;
    }
    const storedIsValidForUser =
      storedLocationId !== undefined &&
      !storedLocationMissing &&
      (isAdmin || locations.some((l) => l.id === storedLocationId));
    if (!storedIsValidForUser) setStoredLocationId(locations[0].id);
  }, [locations, user, isAdmin, storedLocationId, storedLocationMissing, setStoredLocationId]);

  React.useEffect(() => {
    if (!viewLocation || !user || !analyticsUser) return;
    void identify({ user: analyticsUser, location: viewLocation });
  }, [viewLocation, user, analyticsUser]);

  return locationsLoading || (!storedLocation && !storedLocationMissing && locations?.length !== 0);
};
